import React, { useState } from 'react';
import { Copy, RefreshCw, Edit2, Check } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * MessageActions Component
 * 
 * Provides contextual actions for messages:
 * - Assistant messages: Copy, Regenerate
 * - User messages: Copy, Edit and Resubmit
 * 
 * IMPORTANT: Edit/Resubmit creates a new message, does NOT modify history.
 * Conversation integrity is preserved.
 */
export default function MessageActions({ 
  message, 
  onRegenerate, 
  onEditSubmit,
  isRegenerating = false,
  className = ""
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(message.content);
  const [copied, setCopied] = useState(false);

  const isUser = message.actor === 'user';
  const isAssistant = message.actor === 'assistant';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleRegenerate = () => {
    if (onRegenerate && !isRegenerating) {
      onRegenerate(message);
    }
  };

  const handleEditSubmit = () => {
    if (editedContent.trim() && editedContent !== message.content) {
      onEditSubmit(editedContent.trim());
      setIsEditing(false);
    } else {
      setIsEditing(false);
    }
  };

  const handleEditCancel = () => {
    setEditedContent(message.content);
    setIsEditing(false);
  };

  // Edit mode for user messages
  if (isUser && isEditing) {
    return (
      <div className="mt-2 space-y-2 max-w-2xl">
        <textarea
          value={editedContent}
          onChange={(e) => setEditedContent(e.target.value)}
          className="w-full p-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          rows={3}
          autoFocus
        />
        <div className="flex gap-2 justify-end">
          <Button
            size="sm"
            variant="ghost"
            onClick={handleEditCancel}
            className="h-7 px-3 text-xs"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleEditSubmit}
            className="h-7 px-3 text-xs"
            disabled={!editedContent.trim() || editedContent === message.content}
          >
            Submit
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ${className}`}
      data-testid={`message-actions-${message.id || 'unknown'}`}
    >
      {/* Copy - available for both */}
      <button
        onClick={handleCopy}
        className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
        title="Copy message"
        data-testid="copy-message-btn"
      >
        {copied ? (
          <Check className="w-3.5 h-3.5 text-green-600" />
        ) : (
          <Copy className="w-3.5 h-3.5 text-gray-500" />
        )}
      </button>

      {/* Assistant-only: Regenerate */}
      {isAssistant && onRegenerate && (
        <button
          onClick={handleRegenerate}
          disabled={isRegenerating}
          className="p-1.5 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
          title="Regenerate response"
          data-testid="regenerate-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 text-gray-500 ${isRegenerating ? 'animate-spin' : ''}`} />
        </button>
      )}

      {/* User-only: Edit */}
      {isUser && onEditSubmit && (
        <button
          onClick={() => setIsEditing(true)}
          className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
          title="Edit and resubmit"
          data-testid="edit-message-btn"
        >
          <Edit2 className="w-3.5 h-3.5 text-gray-500" />
        </button>
      )}
    </div>
  );
}
